import {
  Flex,
  Select,
  Text,
  TextInput,
  TextInputProps,
  createForm,
  useToasts
} from '@applyboard/crystal-ui'
import { HasLanguageProficiencyCodes, RequestedDocuments } from '@applyboard/school-applications-types-lib'
import { LanguageProficiencyType } from '@backend/agents-ui-api/resources/customization'
import { isEmpty, includes } from 'lodash'
import { IAdditionalDocumentDialog, ILanguageTestDocumentDialogForm } from '../types'
import {
  documentTypeOptions,
  getLanguageProficiencyFieldsInfo,
} from '../../../../utils'
import { FileUploadField, FileUploadFieldValue } from '../../ApplicationForms/FileUploadField'
import { DocumentDialog } from '../DocumentDialog'
import { ComponentType, useEffect, useRef, useState } from 'react'
import { useUpdateRequestedDocuments } from '../../../../hooks'
import { useCreateLanguageProficiency } from '../../../../hooks/useCreateLanguageProficiency'
import { nanoid } from 'nanoid'
import { useApplicationFormContext } from '../../ApplicationForms/ApplicationForm'
import { ChangeLanguageTestDialog } from '../../ChangeLanguageTestDialog'
import { Asterisk } from '../../../Asterisk'
import { ALLOWED_FILE_TYPES } from '../../../../utils/constants'
import { convertObjToListWithSpecificLastItem } from '../../ApplicationForms/utils'
import { CreateLanguageProficiencyRequestAttributes } from '@backend/agents-ui-api/resources/student-document'

type LanguageTestDocumentsFormFields = {
  testType: string
  documents: FileUploadFieldValue[]
  certificateNumber: string
  description: string
}

const defaultValue = {
  testType: '',
  documents: [],
  certificateNumber: '',
  description: '',
}

const { Form, Field, useFieldValues, useSetFieldValues } =
  createForm<LanguageTestDocumentsFormFields>()

export function LanguageTestDocumentsDialog(props: Readonly<IAdditionalDocumentDialog>) {
  const toast = useToasts()
  const [open, setOpen] = useState(false)
  const { pendingFileUploadState, resetFiles } = useApplicationFormContext()
  const { isUpdatingApplication, updateRequestedDocuments } = useUpdateRequestedDocuments({
    applicationId: props.application.id,
    index: props.index,
  })
  const { createLanguageProficiencyAsync, isCreatingLanguageProficiency } = useCreateLanguageProficiency()

  const languageTestId = useRef<string>()
  useEffect(() => {
    languageTestId.current = nanoid()
  }, [])

  useEffect(() => {
    resetFiles({})
  }, [resetFiles])

  const requestedDocuments = props.application.attributes?.requestedDocuments?.[props.id] as RequestedDocuments

  return (
    <DocumentDialog
      document={props.document}
      heading={`Add ${documentTypeOptions?.[props.document.documentType]?.label}`}
      loading={isUpdatingApplication || isCreatingLanguageProficiency}
      open={open}
      setOpen={open => {
        resetFiles({})
        setOpen(open)
      }}
      form={`language-test-documents-${languageTestId}`}
    >
      <Flex direction="column" gap={4}>
        <Text contrast="mid">{props.document.note}</Text>
        <Form
          defaultValues={defaultValue}
          validationMode={'onSubmit'}
          onSubmit={async data => {
            // Get the uploaded file for the language proficiency document
            const documentFile = data.documents[0]?.file
            
            if (!documentFile) {
              return // Should be handled by validation
            }

            // Only include certificateNumber for specific test types
            const TEST_TYPES_REQUIRING_CERTIFICATE_NUMBER = ['IELTS', 'PTE', 'TOEFL', 'DUOLINGO'];
            const attributes: CreateLanguageProficiencyRequestAttributes['attributes'] = {
              contentType: documentFile.type,
              fileName: documentFile.name,
              documentId: languageTestId.current as string,
              fileSize: documentFile.size,
              testType: data.testType as LanguageProficiencyType,
              birthDate: props.application.attributes?.personalInformation?.basicPersonalInformation?.dateOfBirth!,
              certificateNumber: includes(TEST_TYPES_REQUIRING_CERTIFICATE_NUMBER, data.testType) ? data.certificateNumber : '',
              givenName: props.application.attributes?.personalInformation?.basicPersonalInformation?.givenName!,
              familyName: props.application.attributes?.personalInformation?.basicPersonalInformation?.familyName,
              middleName: props.application.attributes?.personalInformation?.basicPersonalInformation?.middleName,
              description: data.description,
            };

            const studentId = props.application.relationships.student?.data?.id;
            if (!studentId) {
              toast.negative(new Error('Student ID is missing. Cannot create language proficiency document.'));
              return;
            }

            // First, create the language proficiency document in the student documents collection
            const creationResponse = await createLanguageProficiencyAsync({
              studentId: studentId,
              data: {
                type: 'language-proficiency',
                attributes,
                file: documentFile,
              },
            })

            // Then, update the requested documents to mark this request as completed
            updateRequestedDocuments({
              additionalData: {
                languageProficiency: {
                  hasLanguageProficiency: 'HAVE' as HasLanguageProficiencyCodes,
                  languageProficiencyData: {
                    [languageTestId.current as string]: {
                      languageProficiencyId: creationResponse.data.id,
                    },
                  },
                },
              },
              dataFiles: { [creationResponse.data.id]: null },
              documentType: props.document.documentType,
              onSuccess: () => {
                setOpen(false)
              },
              requestedDocuments,
              requestedDocumentsId: props.id,
              sectionReference: languageTestId.current,
            })
          }}
          id={`language-test-documents-${languageTestId}`}
        >
          <LanguageTestDocumentsDialogForm
            languageTestId={languageTestId.current as string}
            application={props.application}
            document={props.document}
          />
        </Form>
      </Flex>
    </DocumentDialog>
  )
}

function LanguageTestDocumentsDialogForm(props: Readonly<ILanguageTestDocumentDialogForm>) {
  const { addPendingDelete, getObservableFiles } = useApplicationFormContext()

  const [dialogData, setDialogData] = useState<{
    currentTestValue: string
    isOpen: boolean
    newTestValue: string | null
  }>({
    currentTestValue: '',
    isOpen: false, // is the dialog open or closed
    newTestValue: null,
  })

  const {
    certificateNumber,
    documents,
    testType,
    description,
  } = useFieldValues([
    'certificateNumber',
    'documents',
    'testType',
    'description',
  ])
  const setFieldValues = useSetFieldValues()

  const testTypeOptions = {
    IELTS: 'IELTS',
    PTE: 'PTE Academic',
    TOEFL: 'TOEFL®️',
    DUOLINGO: 'DUOLINGO',
    CAEL: 'CAEL',
    CAMBRIDGE: 'Cambridge',
    MET: 'MET',
    EPP: 'English Pathway Program',
    CANTEST: 'CanTEST',
    LANGUAGECERT_ESOL: 'LanguageCert International ESOL',
    LEAP_4_ADVANCED: 'LEAP 4 Advanced',
    OTHER: 'Other',
  }


  return (
    <Flex direction="column" gap={6}>
      <Flex.Item basis="100%">
        <Field
          as={Select}
          label="Proof type"
          name={`testType`}
          appearance="styled"
          required="Proof type is required"
          onChange={value => {
            if (
              testType.length !== 0 &&
              (
                !isEmpty(certificateNumber) ||
                !isEmpty(description) ||
                !isEmpty(documents)
              )
            ) {
              setDialogData({
                currentTestValue: testType,
                isOpen: true,
                newTestValue: (value as string | undefined) || null,
              })
            }
          }}
        >
          {convertObjToListWithSpecificLastItem(testTypeOptions, 'OTHER').map(([value, label]) => (
            <Select.Option value={value} label={label} key={value} />
          ))}
        </Field>
      </Flex.Item>
      <SelectedLanguageTestFields />
      <SelectedOtherTypeFields />
      <UploadField application={props.application} languageTestId={props.languageTestId} />
      <ChangeLanguageTestDialog
        currentTestLabel={
          testTypeOptions[dialogData.currentTestValue as keyof typeof testTypeOptions]
        }
        isOpen={dialogData.isOpen}
        newTestLabel={
          dialogData.newTestValue
            ? testTypeOptions[dialogData.newTestValue as keyof typeof testTypeOptions]
            : ''
        }
        onCloseDialog={(isOpen: boolean) => {
          setFieldValues({
            testType: dialogData.currentTestValue,
          })

          setDialogData({
            currentTestValue: '',
            isOpen: isOpen,
            newTestValue: null,
          })
        }}
        onConfirm={() => {
          const fieldsInfo = getLanguageProficiencyFieldsInfo(dialogData.currentTestValue)

          for (const id  of Object.keys(
            getObservableFiles({
              fileType: fieldsInfo?.fileType,
              sectionReference: props.languageTestId,
            }),
          )) {
            addPendingDelete(id)
          }

          setFieldValues({
            ...defaultValue,
            testType: dialogData.newTestValue || '',
          })

          setDialogData({
            currentTestValue: '',
            isOpen: false,
            newTestValue: null,
          })
        }}
      />
    </Flex>
  )
}


function SelectedLanguageTestFields() {
  const { testType } = useFieldValues(['testType'])

  if (!includes(['IELTS', 'PTE', 'TOEFL', 'DUOLINGO'], testType)) {
    return null
  }

  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  const certificateLabel = fieldsInfo?.codeLabel || 'Certificate number'

  return (
    <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%' }}>
        <Field
          as={TextInput as ComponentType<TextInputProps>}
          label={certificateLabel}
          name="certificateNumber"
          required={`${certificateLabel} is required`}
        />
      </Flex.Item>
    </Flex>
  )
}

function SelectedOtherTypeFields() {
  const { testType } = useFieldValues(['testType'])
  if (testType !== 'OTHER') {
    return null
  }
  return (
    <Flex basis="100%" gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%' }}>
        <Field
          as={TextInput as ComponentType<TextInputProps>}
          label="Please specify other proof type"
          name="description"
          helpText="Provide a description with up to 200 characters"
          validate={value => {
            if (value.length > 200) {
              return 'This field has a character limit of 200'
            }
            return true
          }}
          required={'This field is required'}
        />
      </Flex.Item>
    </Flex>
  )
}


type UploadFieldProps = {
  application: any
  languageTestId: string
}

function UploadField(props: Readonly<UploadFieldProps>) {
  const { testType, documents } = useFieldValues(['testType', 'documents'])
  const setFieldValues = useSetFieldValues()
  const { getObservableFiles } = useApplicationFormContext()
  const fieldsInfo = getLanguageProficiencyFieldsInfo(testType)
  
  if (!fieldsInfo) {
    return null
  }

  return (
    <Field
      as={FileUploadField}
      allowedFileTypes={ALLOWED_FILE_TYPES}
      application={props.application}
      fileLimit={1}
      fileType={fieldsInfo.fileType}
      label={
        <Text variant="bodyM">
          Add the language proficiency document below, supported file formats: JPG, JPEG, PDF,
          PNG, max number of files: 1 <Asterisk />
        </Text>
      }
      name={`documents`}
      onRemove={(id: string) =>
        setFieldValues({ documents: documents.filter(file => file.id !== id) })
      }
      section={props.languageTestId}
      showHistory={false}
      validate={value => {
        const observableFiles = getObservableFiles({
          fileType: fieldsInfo.fileType,
          sectionReference: props.languageTestId,
        })
        if (Object.keys(observableFiles).length > 1) {
          return `This field has a file limit of ${1}.`
        }
        if (!(Object.keys(observableFiles).length || value.length)) {
          return 'This field is required'
        }
        return true
      }}
    />
  )
}
